/* eslint-disable */
import * as signalR from '@microsoft/signalr'
import settings from '@nobia/zeus-components/lib/settings'

import {
  isAuthTimeoutError,
  onAuthRefreshSuccess,
  refreshAccessToken,
  refreshIfSessionExpired,
} from './auth419Recovery'
import { canAttemptSignalRStart } from './signalrAuthGuards'

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000]

class Connector {
  #connection = null

  #listeners = new Map()

  #listenersRegistered = false

  #authExpired = false

  #removeRefreshListener = null

  constructor() {
    console.log(
      'SignalR: Checking the user status before creating the connection (if messaging is enabled)'
    )
    this.#removeRefreshListener = onAuthRefreshSuccess(() => {
      this.#authExpired = false
      if (this.#listeners.size > 0) {
        this.start().catch(console.error)
      }
    })
  }

  #getConnection() {
    if (!this.#connection) {
      this.#connection = new signalR.HubConnectionBuilder()
        .withUrl(`${settings.myProjectApi.slice(0, -4)}/notification/`, {
          accessTokenFactory: async () => {
            await refreshIfSessionExpired()
            return window.__OMNI__?.access_token
          },
        })
        .configureLogging(signalR.LogLevel.Debug)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (isAuthTimeoutError(retryContext.retryReason)) {
              this.#handleAuthTimeout()
              return null
            }
            const index = Math.min(
              retryContext.previousRetryCount,
              RECONNECT_DELAYS_MS.length - 1
            )
            return RECONNECT_DELAYS_MS[index]
          },
        })
        .build()

      if (!this.#listenersRegistered) {
        this.#setupListeners()
        this.#listenersRegistered = true
      }

      console.log('SignalR: Create connector')
    }
    return this.#connection
  }

  #handleAuthTimeout() {
    this.#authExpired = true
    refreshAccessToken().catch(() => {})
  }

  async #start() {
    if (!canAttemptSignalRStart(this.#authExpired)) {
      return false
    }

    const connection = this.#getConnection()
    if (connection.state !== signalR.HubConnectionState.Disconnected) {
      return connection.state === signalR.HubConnectionState.Connected
    }
    try {
      await connection.start()
      console.log('SignalR: started')
      this.#authExpired = false
      return true
    } catch (error) {
      console.error(error)
      if (isAuthTimeoutError(error)) {
        this.#handleAuthTimeout()
      }
      return false
    }
  }

  async #stop() {
    if (!this.#connection) return
    try {
      await this.#connection.stop()
      console.log('SignalR: stopped')
    } catch (error) {
      console.error(error)
    }
  }

  #setupListeners() {
    const connection = this.#connection
    connection.onreconnecting((error) => {
      console.log('SignalR: Connection lost. Reconnecting.', error)
    })

    connection.onreconnected(async (connectionId) => {
      console.log('SignalR: Reconnected. Connection ID:', connectionId)
      this.#authExpired = false
      for (const [key, cb] of this.#listeners.entries()) {
        if (key === 'Reconnect') {
          cb()
        } else {
          await this.#subscribe(key)
        }
      }
    })

    connection.onclose((error) => {
      console.log('SignalR: Connection closed.', error)
      if (isAuthTimeoutError(error)) {
        this.#handleAuthTimeout()
      }
    })

    connection.off('ChatMessage')
    connection.on('ChatMessage', (data) => {
      console.log('SignalR: Received ChatMessage', data)
      for (const [k, cb] of this.#listeners.entries()) {
        if (k === 'Reconnect') {
          continue
        }
        if (typeof cb === 'function') {
          cb(data)
        }
      }
    })
  }

  async #subscribe() {
    if (this.#authExpired) {
      return
    }

    const connection = this.#getConnection()
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      const started = await this.#start()
      if (!started) {
        return
      }
    }

    while (
      connection.state === signalR.HubConnectionState.Connecting ||
      connection.state === signalR.HubConnectionState.Reconnecting
    ) {
      console.log('SignalR: Connection is not ready')
      await sleep(100)
    }

    if (connection.state !== signalR.HubConnectionState.Connected) {
      console.log('SignalR: Failed connection ' + connection.state)
      return
    }

    console.log('SignalR: Subscribe')
    await connection.send('SubscribeUser', settings.brand)
  }

  id() {
    return this.#connection?.connectionId
  }

  state() {
    return this.#connection?.state
  }

  async start() {
    console.log('SignalR: start listening')
    const started = await this.#start()
    if (!started || !this.#connection) {
      return
    }

    if (this.#connection.state !== signalR.HubConnectionState.Connected) {
      return
    }
  }

  async close() {
    if (!this.#connection) return
    this.#connection.off('ChatMessage')
    await this.#stop()
    this.#connection = null
    this.#listenersRegistered = false
  }

  async onReconnect(callback) {
    this.#listeners.set('Reconnect', callback)
  }

  async offReconnect() {
    this.#listeners.delete('Reconnect')
  }

  async subscribe(callback) {
    await this.#subscribe()
    this.#listeners.set('Subscribe', callback)
  }

  async unsubscribe() {
    if (this.#connection) {
      await this.#connection.send('UnsubscribeUser', settings.brand)
    }
    this.#listeners.delete('Subscribe')

    if (!this.#listeners.size) {
      await this.close()
    }
  }
}

export const Connection = new Connector()
